export class Helper {
  /**
   * Simple filter function for plain objects
   */
  static filter(objects, filter) {
    return objects?.filter((element) => {
      for (const [key, value] of Object.entries(filter)) {
        if (Array.isArray(element[key])) {
          for (const item of element[key]) {
            if (!element[key].includes(value)) {
              return false;
            }
          }
        } else if (element[key] !== value) {
          return false;
        }
      }
      return true;
    }) || [];
  }
  /**
   * Convert to a url/uri conform string
   * Only:
   * - lower case
   * - chars, numbers and hyphens
   */
  static urlString(str, options = {}) {
    const config = {
      allowAll: false,
      ...options
    };
    str = str + "";
    str = str.trim();
    str = str.toLowerCase();
    if (!config.allowAll) {
      str = str.trim().replace(/\s+/g, "-");
      str = str.replace("\xE4", "ae");
      str = str.replace("\xF6", "oe");
      str = str.replace("\xFC", "ue");
      str = str.replace("\xDF", "ss");
      str = str.replace("---", "-");
      str = str.replace("--", "-");
      str = str.replace(/[^a-zA-Z0-9-]/g, "");
    }
    return encodeURI(str);
  }
  /**
   * Simple map function
   */
  static map(source, target, options = {}) {
    const config = {
      dateStringToDate: true,
      funcAllowed: false,
      ...options
    };
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return target;
    }
    for (const key of Object.keys(target)) {
      if (source[key] !== void 0 && (config.funcAllowed || typeof (source[key] !== "function"))) {
        if (typeof source[key] === "string" && config.dateStringToDate) {
          if (Array.isArray(config.dateStringToDate)) {
            if (config.dateStringToDate.includes(key)) {
              target[key] = Helper.parseDate(source[key]);
            }
          } else {
            target[key] = Helper.parseDate(source[key]);
          }
        } else {
          target[key] = source[key];
        }
      }
    }
    return target;
  }
  /**
   * Create Object or Objects of specified type with specified data
   */
  static maps(data, targetClass) {
    if (!data || typeof data !== "object") {
      return void 0;
    }
    if (!Array.isArray(data)) {
      data = [data];
    }
    return data.map((item) => {
      return targetClass.map(item);
    });
  }
  /**
   * Get plain parameter
   */
  static getPlainParameter(parameter) {
    if (!parameter) {
      return parameter;
    }
    return parameter?.split("?")?.shift()?.split("#")?.shift();
  }
  /**
   * Get ID of an url parameter
   */
  static getParameterId(parameter) {
    if (!parameter) {
      return parameter;
    }
    return Helper.getPlainParameter(parameter)?.split("-").pop();
  }
  /**
   * Delay
   */
  static delay(ms) {
    const promise = new Promise((resolve) => setTimeout(resolve, ms));
    return promise;
  }
  /**
   * Deep freeze
   * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze#What_is_shallow_freeze
   */
  static deepFreeze(object, preparedObjects = []) {
    if (preparedObjects.includes(object)) {
      return object;
    }
    preparedObjects.push(object);
    const propNames = Object.getOwnPropertyNames(object);
    for (const name of propNames) {
      const value = object[name];
      if (value && typeof value === "object") {
        Helper.deepFreeze(value, preparedObjects);
      }
    }
    return Object.freeze(object);
  }
  /**
   * Generates an unique identifier with a specific length
   */
  static getUID(length = 24) {
    let dt = (/* @__PURE__ */ new Date()).getTime();
    const uuid = "x".repeat(length).replace(/[xy]/g, (c) => {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === "x" ? r : r & 3 | 8).toString(16);
    });
    return uuid;
  }
  /**
   * Parse date string to date object
   */
  static parseDate(dateString) {
    const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
    const reMsAjax = /^\/Date\((d|-|.*)\)[/|\\]$/;
    if (typeof dateString === "string") {
      const isoExec = reISO.exec(dateString);
      if (isoExec) {
        return new Date(dateString);
      }
      const msAjaxExec = reMsAjax.exec(dateString);
      if (msAjaxExec) {
        const cleaned = msAjaxExec[1].split(/[-+,.]/);
        return new Date(cleaned[0] ? +cleaned[0] : 0 - +cleaned[1]);
      }
    }
    return dateString;
  }
  /**
   * Get browser name and version
   */
  static getBrowser() {
    const userAgent = navigator.userAgent;
    let data = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    let version;
    if (/trident/i.test(data[1])) {
      version = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
      return { name: "IE", version: version[1] || "" };
    }
    if (data[1] === "Chrome") {
      version = userAgent.match(/\bOPR|Edge\/(\d+)/);
      if (version != null) {
        return { name: "Opera", version: version[1] };
      }
    }
    data = data[2] ? [data[1], data[2]] : [navigator.appName, navigator.appVersion, "-?"];
    version = userAgent.match(/version\/(\d+)/i);
    if (version !== null) {
      data.splice(1, 1, version[1]);
    }
    return {
      name: data[0],
      version: data[1]
    };
  }
  /**
   * Whether first parameter is a deep subset of second parameter
   * Hint: functions will be ignored
   */
  static isSubset(a, b) {
    if (a === b) {
      return true;
    }
    if (typeof a !== typeof b) {
      return false;
    }
    if (Array.isArray(a)) {
      for (const [key, value] of Object.entries(a)) {
        if (Helper.isSubset(value, b[key])) {
          return false;
        }
      }
      return true;
    }
    if (typeof a === "object") {
      const plain = JSON.parse(JSON.stringify(a));
      for (const [key, value] of Object.entries(plain)) {
        if (Helper.isSubset(value, b[key])) {
          return false;
        }
      }
      return true;
    }
    return true;
  }
  /**
   * Whether parameters are equal (have equal properties)
   * Hint: functions will be ignored
   */
  static isEqual(a, b) {
    return Helper.isSubset(a, b) && Helper.isSubset(b, a);
  }
  /**
   * First level difference of two objects
   * Hint: functions will be ignored and when an array or an object is different the hole array or object will used as property value
   *
   * @returns Record<string, any> Object with properties from first object
   */
  static getDiff(a, b, clone = true) {
    const result = {};
    let firstObject = a;
    if (clone) {
      firstObject = JSON.parse(JSON.stringify(a));
    }
    for (const [key, value] of Object.entries(firstObject)) {
      if (!Helper.isSubset(value, b[key])) {
        result[key] = value;
      }
    }
    return result;
  }
  /**
   * Check if propertie is type of HTMLCollection or NodeList
   * See https://stackoverflow.com/a/7238344
   *
   * @param nodes
   */
  static isNodeList(nodes) {
    try {
      const stringRepr = Object.prototype.toString.call(nodes);
      return typeof nodes === "object" && /^\[object (HTMLCollection|NodeList|Object)\]$/.test(stringRepr) && typeof nodes.length === "number" && (nodes.length === 0 || typeof nodes[0] === "object" && nodes[0].nodeType > 0);
    } catch (e) {
      return false;
    }
  }
  static arrayBufferToBase64(buffer) {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return `data:image/*;base64, ${window.btoa(binary)}`;
  }
}
